import { useState, useEffect, useCallback, useRef } from 'react'
import { XRService } from '@/services/xr/xr-service'
import { DeviceOrientationAR } from '@/services/xr/device-orientation-ar'
import type { XRSessionState, XROptions } from '@/types/xr'

interface UseXROptions extends XROptions {
  autoCheck?: boolean
  enableFallback?: boolean
  onDebugLog?: (message: string) => void
}

interface ExtendedXRState extends XRSessionState {
  webXRSupported: boolean
  arModeAvailable: 'webxr' | 'device-orientation' | 'none'
  vrModeAvailable: boolean
  isIOS: boolean
  hasMotionPermission: boolean
}

export function useXR(options: UseXROptions = {}) {
  const [state, setState] = useState<ExtendedXRState>({
    isSupported: false,
    isActive: false,
    sessionType: null,
    error: null,
    webXRSupported: false,
    arModeAvailable: 'none',
    vrModeAvailable: false,
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
    hasMotionPermission: false
  })
  
  const xrServiceRef = useRef<XRService | null>(null)
  const deviceOrientationARRef = useRef<DeviceOrientationAR | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const modelUrlRef = useRef<string | null>(null)

  // Debug logging helper - using ref to avoid recreating
  const debugLogRef = useRef((message: string) => {
    console.log(`[useXR] ${message}`)
    options.onDebugLog?.(message)
  })
  
  // Update the ref when options change
  useEffect(() => {
    debugLogRef.current = (message: string) => {
      console.log(`[useXR] ${message}`)
      options.onDebugLog?.(message)
    }
  }, [options.onDebugLog])
  
  const debugLog = useCallback((message: string) => {
    debugLogRef.current(message)
  }, [])

  // Check support callback should be defined before useEffect
  const checkSupportFn = useCallback(async () => {
    // debugLog('Starting XR support check...')
    
    const newState: Partial<ExtendedXRState> = {
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent)
    }
    
    // Check WebXR support
    if (navigator.xr) {
      // debugLog('navigator.xr found, checking session support...')
      try {
        const [vrSupported, arSupported] = await Promise.all([
          navigator.xr.isSessionSupported('immersive-vr'),
          navigator.xr.isSessionSupported('immersive-ar')
        ])
        
        // debugLog(`VR supported: ${vrSupported}, AR supported: ${arSupported}`)
        
        newState.webXRSupported = vrSupported || arSupported
        newState.vrModeAvailable = vrSupported
        newState.isSupported = vrSupported || arSupported
        
        if (arSupported) {
          newState.arModeAvailable = 'webxr'
          // debugLog('WebXR AR mode available')
        }
      } catch (error) {
        console.error('WebXR check failed:', error)
        debugLog(`WebXR check error: ${error instanceof Error ? error.message : 'Unknown error'}`)
      }
    } else {
      // debugLog('navigator.xr not found')
    }
    
    // Check for fallback AR support
    if (newState.arModeAvailable === 'none' && options.enableFallback !== false) {
      // debugLog('Checking fallback AR support...')
      
      // Check for device orientation and camera access
      const hasOrientation = typeof DeviceOrientationEvent !== 'undefined'
      const hasCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      
      debugLog(`Device orientation: ${hasOrientation}, Camera: ${hasCamera}`)
      
      if (hasOrientation && hasCamera) {
        newState.arModeAvailable = 'device-orientation'
        newState.isSupported = true
        debugLog('Device Orientation AR mode available')
        
        // Check motion permission on iOS
        if (newState.isIOS && typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
          newState.hasMotionPermission = false // Will be set true after permission granted
          debugLog('iOS device - motion permission required')
        } else {
          newState.hasMotionPermission = true
        }
      }
    }
    
    setState(prev => ({ ...prev, ...newState }))
    return newState as ExtendedXRState
  }, [options.enableFallback])
  
  // Initialize XR service
  useEffect(() => {
    xrServiceRef.current = new XRService(options)
    
    // Check support if autoCheck is enabled
    if (options.autoCheck !== false) {
      checkSupportFn()
    }

    // Cleanup
    return () => {
      if (xrServiceRef.current && state.isActive) {
        xrServiceRef.current.endSession()
      }
    }
  }, []) // Remove dependencies to run only once on mount

  // Alias for public API
  const checkSupport = checkSupportFn

  // Enter VR
  const enterVR = useCallback(async (canvas: HTMLCanvasElement, modelUrl: string) => {
    if (!xrServiceRef.current) return
    
    canvasRef.current = canvas
    modelUrlRef.current = modelUrl
    
    try {
      await xrServiceRef.current.startSession(canvas, modelUrl, 'vr')
      const xrState = xrServiceRef.current.getState()
      setState(prev => ({
        ...prev,
        isActive: xrState.isActive,
        sessionType: xrState.sessionType,
        error: xrState.error
      }))
    } catch (error) {
      console.error('Failed to enter VR:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to enter VR'
      }))
    }
  }, [])

  // Enter AR with fallback support
  const enterAR = useCallback(async (canvas: HTMLCanvasElement, modelUrl: string) => {
    debugLog('enterAR called')
    canvasRef.current = canvas
    modelUrlRef.current = modelUrl
    
    // Try WebXR first if available
    if (state.arModeAvailable === 'webxr' && xrServiceRef.current) {
      debugLog('Attempting WebXR AR session...')
      try {
        await xrServiceRef.current.startSession(canvas, modelUrl, 'ar')
        const xrState = xrServiceRef.current!.getState()
        debugLog(`WebXR AR session started successfully: ${xrState.sessionType}`)
        setState(prev => ({
          ...prev,
          isActive: xrState.isActive,
          sessionType: xrState.sessionType,
          error: xrState.error
        }))
        return
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        console.error('WebXR AR failed, trying fallback:', error)
        debugLog(`WebXR AR failed: ${errorMsg}`)
        
        // Store error for display
        setState(prev => ({
          ...prev,
          error: `WebXR AR failed: ${errorMsg}`
        }))
      }
    } else {
      debugLog(`WebXR AR not available. arModeAvailable: ${state.arModeAvailable}`)
    }
    
    // Try Device Orientation AR fallback
    if (state.arModeAvailable === 'device-orientation' || options.enableFallback) {
      debugLog('Attempting Device Orientation AR fallback...')
      try {
        if (!deviceOrientationARRef.current) {
          debugLog('Creating DeviceOrientationAR instance...')
          deviceOrientationARRef.current = new DeviceOrientationAR({
            onSessionStart: () => {
              debugLog('Device Orientation AR session started')
              setState(prev => ({
                ...prev,
                isActive: true,
                sessionType: 'device-orientation-ar' as any,
                error: null
              }))
            },
            onSessionEnd: () => {
              debugLog('Device Orientation AR session ended')
              setState(prev => ({
                ...prev,
                isActive: false,
                sessionType: null
              }))
            },
            onError: (error) => {
              debugLog(`Device Orientation AR error: ${error.message}`)
              setState(prev => ({
                ...prev,
                error: error.message
              }))
            }
          })
        }
        
        await deviceOrientationARRef.current.startSession(canvas)
        debugLog('Device Orientation AR session request sent')
        return
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to start AR'
        console.error('Device Orientation AR failed:', error)
        debugLog(`Device Orientation AR failed: ${errorMsg}`)
        setState(prev => ({
          ...prev,
          error: errorMsg
        }))
      }
    }
    
    // No AR available
    setState(prev => ({
      ...prev,
      error: 'AR not supported on this device'
    }))
  }, [state.arModeAvailable, options.enableFallback])

  // Exit XR
  const exitXR = useCallback(async () => {
    try {
      // Exit WebXR session if active
      if (xrServiceRef.current && state.sessionType && (state.sessionType === 'immersive-vr' || state.sessionType === 'immersive-ar')) {
        await xrServiceRef.current.endSession()
        const xrState = xrServiceRef.current!.getState()
        setState(prev => ({
          ...prev,
          isActive: xrState.isActive,
          sessionType: xrState.sessionType,
          error: xrState.error
        }))
      }
      
      // Exit Device Orientation AR if active
      if (deviceOrientationARRef.current && state.sessionType === 'device-orientation-ar') {
        await deviceOrientationARRef.current.endSession()
      }
    } catch (error) {
      console.error('Failed to exit XR:', error)
    }
  }, [state.sessionType])

  // Set world transform
  const setWorldTransform = useCallback((transform: Float32Array) => {
    if (xrServiceRef.current) {
      xrServiceRef.current.setWorldTransform(transform)
    }
  }, [])

  // Get device orientation AR instance (for advanced usage)
  const getDeviceOrientationAR = useCallback(() => {
    return deviceOrientationARRef.current
  }, [])

  return {
    state,
    checkSupport,
    enterVR,
    enterAR,
    exitXR,
    setWorldTransform,
    getDeviceOrientationAR,
    canvasRef,
    modelUrlRef
  }
}